import React, { useCallback, useEffect, useState } from "react";
import { Link2, Plus, X, Copy, Mail, Loader2, MessageCircle, CheckCircle2, Clock, CircleDashed, Ban, Eye } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PRESETS = ["Passport Copy", "Proof of Address", "Bank Statement", "Utility Bill", "Company Documents", "Video Recording (15 sec)"];

const STATUS_BADGE = {
  pending: { t: "Pending", c: "border-amber-200 bg-amber-50 text-amber-700", Icon: Clock },
  partial: { t: "Partially uploaded", c: "border-blue-200 bg-blue-50 text-blue-700", Icon: CircleDashed },
  completed: { t: "Completed", c: "border-green-200 bg-green-50 text-green-700", Icon: CheckCircle2 },
};

export default function UploadLinkManager({ req }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(null);
  const [email, setEmail] = useState(req.client_email || "");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [docs, setDocs] = useState(["Passport Copy", "Proof of Address"]);
  const [docInput, setDocInput] = useState("");
  const [videoInstructions, setVideoInstructions] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/upload-links", { params: { req_id: req.id } });
      setLinks(r.data.links || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [req.id]);

  useEffect(() => { load(); }, [load]);

  const addDoc = (name) => {
    const n = (name || "").trim();
    if (!n) return;
    if (docs.some((d) => d.toLowerCase() === n.toLowerCase())) return;
    setDocs((d) => [...d, n]);
    setDocInput("");
  };
  const removeDoc = (i) => setDocs((d) => d.filter((_, x) => x !== i));

  const generate = async () => {
    if (docs.length === 0) return toast.error("Add at least one document.");
    if (!email || !email.includes("@")) return toast.error("Enter the client's email.");
    setCreating(true);
    try {
      await api.post("/admin/upload-links", {
        req_id: req.id, client_email: email, client_phone: phone, message, documents: docs,
        video_instructions: videoInstructions,
      });
      toast.success("Secure upload link generated.");
      setMessage("");
      setVideoInstructions("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create link."); } finally { setCreating(false); }
  };

  const copy = (url) => { navigator.clipboard?.writeText(url); toast.success("Link copied."); };

  const sendEmail = async (token) => {
    setSending(token);
    try { await api.post(`/admin/upload-links/${token}/send-email`); toast.success("Email sent to the client."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send email."); } finally { setSending(null); }
  };

  const whatsapp = (l) => {
    const digits = (l.client_phone || "").replace(/[^0-9]/g, "");
    const text = encodeURIComponent(
      `Hi ${l.client_name || "there"}, please upload your documents for UniIncorp using this secure link: ${l.public_url}`
    );
    window.open(`https://wa.me/${digits}?text=${text}`, "_blank", "noopener");
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this link? The client will no longer be able to use it.")) return;
    try { await api.post(`/admin/upload-links/${token}/revoke`); toast.success("Link revoked."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke link."); }
  };

  return (
    <div className="mt-3 rounded-lg border border-border bg-secondary/30 p-4 space-y-4" data-testid={`ul-manager-${req.id}`}>
      {/* Builder */}
      <div className="rounded-md border border-border bg-card p-3 space-y-3">
        <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5 text-orange-700" /> Generate upload link</p>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Client email" className="h-8 bg-background border-border text-sm" data-testid={`ul-email-${req.id}`} />
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Client WhatsApp number (optional)" className="h-8 bg-background border-border text-sm" data-testid={`ul-phone-${req.id}`} />
        </div>
        <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Short message to the client (optional)" className="h-8 bg-background border-border text-sm" data-testid={`ul-message-${req.id}`} />

        <div className="flex flex-wrap gap-1.5">
          {docs.map((d, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-sm border border-orange-200 bg-orange-50 px-2 py-1 text-xs text-orange-800" data-testid={`ul-chip-${req.id}-${i}`}>
              {d}
              <button onClick={() => removeDoc(i)} className="hover:text-red-600" data-testid={`ul-chip-remove-${req.id}-${i}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <Input value={docInput} onChange={(e) => setDocInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addDoc(docInput))} placeholder="Add a document name…" className="h-8 bg-background border-border text-sm" data-testid={`ul-doc-input-${req.id}`} />
          <Button size="sm" variant="outline" onClick={() => addDoc(docInput)} className="h-8 rounded-sm border-slate-300 bg-card text-xs shrink-0" data-testid={`ul-doc-add-${req.id}`}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.filter((p) => !docs.some((d) => d.toLowerCase() === p.toLowerCase())).map((p) => (
            <button key={p} onClick={() => addDoc(p)} className="rounded-sm border border-dashed border-slate-300 bg-card px-2 py-0.5 text-[11px] text-muted-foreground hover:border-orange-300 hover:text-orange-700" data-testid={`ul-preset-${req.id}-${p.replace(/\s+/g, "-")}`}>+ {p}</button>
          ))}
        </div>
        {docs.some((d) => d.toLowerCase() === "video recording (15 sec)") && (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Video Instructions</label>
            <Input value={videoInstructions} onChange={(e) => setVideoInstructions(e.target.value)} placeholder="E.g. Please record a short video showing your face clearly and reading out your full name and date of birth." className="mt-1 h-8 bg-background border-border text-sm" data-testid={`ul-video-instructions-${req.id}`} />
          </div>
        )}
        <Button size="sm" disabled={creating} onClick={generate} className="rounded-sm bg-primary text-primary-foreground h-8 text-xs" data-testid={`ul-generate-${req.id}`}>
          {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Link2 className="h-3.5 w-3.5 mr-1" /> Generate secure link</>}
        </Button>
      </div>

      {/* Existing links */}
      {loading ? (
        <div className="py-4 grid place-items-center"><Loader2 className="h-4 w-4 animate-spin text-orange-600" /></div>
      ) : links.length === 0 ? (
        <p className="text-xs text-muted-foreground">No upload links yet for this request.</p>
      ) : (
        <div className="space-y-2">
          {links.map((l) => {
            const b = STATUS_BADGE[l.status] || STATUS_BADGE.pending;
            return (
              <div key={l.token} className="rounded-md border border-border bg-card p-3" data-testid={`ul-link-${l.token}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-wider ${b.c}`} data-testid={`ul-status-${l.token}`}><b.Icon className="h-3 w-3" /> {b.t}</span>
                    <span className="text-xs text-muted-foreground">{l.uploaded_count}/{l.doc_count} uploaded</span>
                    {l.opened
                      ? <span className="inline-flex items-center gap-1 rounded-sm border border-green-200 bg-green-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-green-700" data-testid={`ul-opened-${l.token}`}><Eye className="h-3 w-3" /> Opened{l.open_count > 1 ? ` ×${l.open_count}` : ""}</span>
                      : <span className="rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-500" data-testid={`ul-not-opened-${l.token}`}>Not opened</span>}
                    {l.revoked
                      ? <span className="text-[10px] uppercase tracking-wider text-red-600 border border-red-200 bg-red-50 rounded-sm px-1.5 py-0.5" data-testid={`ul-revoked-${l.token}`}>Revoked</span>
                      : l.expired && <span className="text-[10px] uppercase tracking-wider text-red-600 border border-red-200 bg-red-50 rounded-sm px-1.5 py-0.5">Expired</span>}
                  </div>
                  <span className="text-[11px] text-muted-foreground">to {l.client_email}</span>
                </div>
                <div className="mt-2 flex items-center gap-1.5 rounded-sm border border-border bg-background px-2 py-1">
                  <span className="text-[11px] font-mono text-muted-foreground truncate flex-1" data-testid={`ul-url-${l.token}`}>{l.public_url}</span>
                  <button onClick={() => copy(l.public_url)} className="text-muted-foreground hover:text-orange-700 shrink-0" data-testid={`ul-copy-${l.token}`}><Copy className="h-3.5 w-3.5" /></button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" disabled={sending === l.token} onClick={() => sendEmail(l.token)} className="h-7 rounded-sm border-slate-300 bg-card text-xs" data-testid={`ul-send-email-${l.token}`}>
                    {sending === l.token ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Mail className="h-3.5 w-3.5 mr-1" /> Email client</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => whatsapp(l)} className="h-7 rounded-sm border-green-300 text-green-700 bg-card hover:bg-green-50 text-xs" data-testid={`ul-whatsapp-${l.token}`}>
                    <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                  </Button>
                  {!l.revoked && !l.expired && (
                    <Button size="sm" variant="outline" onClick={() => revoke(l.token)} className="h-7 rounded-sm border-red-200 text-red-600 bg-card hover:bg-red-50 text-xs" data-testid={`ul-revoke-${l.token}`}>
                      <Ban className="h-3.5 w-3.5 mr-1" /> Revoke
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
